import { useEffect, useMemo, useState } from "react";
import { api, type MediaItem, type Playlist, type PlaylistItem } from "../api";
import { Icon } from "../icons";
import { formatBytes } from "../format";
import { Banner, EmptyState, PageHeader, SearchField } from "../ui";

type DraftItem = {
  key: string;
  mediaId: string;
  name: string;
  type: "video" | "image";
  size: number;
  durationSeconds: number | null;
};

export function PlaylistsPage({ path, navigate }: { path: string; navigate: (to: string) => void }) {
  const playlistId = path.startsWith("/playlists/") ? decodeURIComponent(path.slice("/playlists/".length)) : "";
  const [playlists, setPlaylists] = useState<Playlist[] | null>(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");

  async function load() {
    const result = await api<{ playlists: Playlist[] }>("/api/playlists");
    setPlaylists(result.playlists);
  }

  useEffect(() => {
    load().catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Chargement impossible"));
  }, []);

  async function create() {
    setError("");
    try {
      const result = await api<{ playlist: Playlist }>("/api/playlists", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      setName("");
      await load();
      navigate(`/playlists/${result.playlist.id}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Création impossible");
    }
  }

  const selected = playlists?.find((playlist) => playlist.id === playlistId) ?? null;

  return (
    <section>
      {playlistId && selected ? null : (
        <PageHeader
          kicker="Ordre de lecture"
          title="Playlists"
          lede="L'ordre dans lequel la télé enchaîne les médias. Un enregistrement s'applique à la fin de l'élément en cours."
        />
      )}
      {error ? <Banner tone="error">{error}</Banner> : null}
      {playlistId && selected ? (
        <Editor
          playlist={selected}
          onBack={() => navigate("/playlists")}
          onSaved={async () => { await load(); }}
          onDeleted={() => { navigate("/playlists"); void load(); }}
        />
      ) : null}
      {playlistId && playlists && !selected ? <Banner tone="error">Playlist introuvable.</Banner> : null}
      {!playlistId ? (
        <>
          <form className="composer card" onSubmit={(event) => { event.preventDefault(); void create(); }}>
            <label>
              <span>Nouvelle playlist</span>
              <span className="field-hint">Un nom clair, comme Accueil ou Cardio.</span>
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Accueil, cardio, vestiaires" required minLength={2} />
            </label>
            <button className="primary" type="submit">Créer</button>
          </form>
          <div className="playlist-grid">
            {(playlists ?? []).map((playlist) => (
              <button key={playlist.id} type="button" className="playlist-card" onClick={() => navigate(`/playlists/${playlist.id}`)}>
                <span className="playlist-card-top">
                  <strong>{playlist.name}</strong>
                  <Icon name="chevron" />
                </span>
                <span className="muted">{countLabel(playlist)}</span>
                <span className="mini-track" aria-hidden="true">
                  {playlist.items.slice(0, 8).map((item) => (
                    <span key={item.id} className={`seg ${item.type}`} />
                  ))}
                </span>
                <span className="meta">{previewLabel(playlist)}</span>
              </button>
            ))}
          </div>
          {playlists && playlists.length === 0 ? (
            <EmptyState icon="list" title="Aucune playlist" text="Donnez-lui un nom ci-dessus, puis ajoutez les médias dans l'ordre de passage." />
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function Editor({
  playlist,
  onBack,
  onSaved,
  onDeleted,
}: {
  playlist: Playlist;
  onBack: () => void;
  onSaved: () => Promise<void>;
  onDeleted: () => void;
}) {
  const [title, setTitle] = useState(playlist.name);
  const [items, setItems] = useState<DraftItem[]>(() => playlist.items.map(toDraft));
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    setTitle(playlist.name);
    setItems(playlist.items.map(toDraft));
  }, [playlist]);

  useEffect(() => {
    api<{ media: MediaItem[] }>("/api/media")
      .then((result) => setMedia(result.media.filter((item) => item.status === "ready")))
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Médias indisponibles"));
  }, []);

  const dirty = useMemo(() => {
    if (title !== playlist.name) return true;
    if (items.length !== playlist.items.length) return true;
    return items.some((item, index) => {
      const saved = playlist.items[index];
      return !saved || saved.mediaId !== item.mediaId || saved.durationSeconds !== item.durationSeconds;
    });
  }, [items, playlist, title]);

  useEffect(() => {
    if (!dirty) return;
    const onLeave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  function back() {
    if (dirty && !window.confirm("Quitter sans enregistrer la playlist ?")) return;
    onBack();
  }

  function move(index: number, direction: -1 | 1) {
    const next = index + direction;
    if (next < 0 || next >= items.length) return;
    const copy = [...items];
    const current = copy[index];
    const target = copy[next];
    if (!current || !target) return;
    copy[index] = target;
    copy[next] = current;
    setItems(copy);
  }

  function add(item: MediaItem) {
    setItems((current) => [
      ...current,
      {
        key: crypto.randomUUID(),
        mediaId: item.id,
        name: item.originalName,
        type: item.type,
        size: item.size,
        durationSeconds: item.type === "image" ? 10 : null,
      },
    ]);
  }

  async function save() {
    setError("");
    setNotice("");
    try {
      if (title !== playlist.name) {
        await api(`/api/playlists/${playlist.id}`, { method: "PATCH", body: JSON.stringify({ name: title }) });
      }
      await api(`/api/playlists/${playlist.id}/items`, {
        method: "PUT",
        body: JSON.stringify({
          items: items.map((item) => ({ mediaId: item.mediaId, durationSeconds: item.durationSeconds })),
        }),
      });
      setNotice("Enregistrée. Les télés l'appliquent à la fin du média en cours.");
      await onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible");
    }
  }

  async function remove() {
    if (!window.confirm(`Supprimer la playlist ${playlist.name} ? Les écrans qui l'utilisent n'auront plus de contenu.`)) return;
    try {
      await api(`/api/playlists/${playlist.id}`, { method: "DELETE" });
      onDeleted();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Suppression impossible");
    }
  }

  const needle = query.trim().toLowerCase();
  const available = media.filter((item) => item.originalName.toLowerCase().includes(needle));

  return (
    <div className="editor">
      <div className="editor-bar">
        <button type="button" className="text-button back" onClick={back}>Playlists</button>
        <div className="row-actions">
          {dirty ? <span className="field-hint">Non enregistrée</span> : null}
          <button type="button" className="primary" disabled={!dirty} onClick={() => void save()}>{dirty ? "Enregistrer" : "Enregistré"}</button>
        </div>
      </div>
      <label className="title-field">
        Nom
        <input value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      {error ? <Banner tone="error">{error}</Banner> : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}
      <div className="editor-grid">
        <div>
          <p className="section-label">Ordre de passage</p>
          <p className="field-hint">Image : secondes à l'écran. Vidéo : laissez vide pour la jouer en entier.</p>
          {items.length === 0 ? <p className="empty-note">Aucun média. Ajoutez-en depuis la bibliothèque, à droite.</p> : null}
          <ol className="order-list">
            {items.map((item, index) => (
              <li key={item.key} className="order-item">
                <span className="order-index">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <strong>{item.name}</strong>
                  <p className="muted">{item.type === "video" ? "Vidéo" : "Image"} · {formatBytes(item.size)}</p>
                </div>
                <label className="duration">
                  Secondes
                  <input
                    type="number"
                    min={1}
                    max={86400}
                    inputMode="numeric"
                    placeholder={item.type === "video" ? "entière" : "10"}
                    aria-label={item.type === "video" ? "Secondes, vide pour la vidéo entière" : "Secondes d'affichage"}
                    value={item.durationSeconds ?? ""}
                    onChange={(event) => {
                      const raw = event.target.value;
                      setItems((current) => current.map((entry) => {
                        if (entry.key !== item.key) return entry;
                        if (raw === "") return { ...entry, durationSeconds: item.type === "image" ? 10 : null };
                        return { ...entry, durationSeconds: Number(raw) };
                      }));
                    }}
                  />
                </label>
                <div className="quiet-actions">
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0}>Monter</button>
                  <button type="button" onClick={() => move(index, 1)} disabled={index === items.length - 1}>Descendre</button>
                  <button type="button" className="danger" onClick={() => setItems((current) => current.filter((entry) => entry.key !== item.key))}>Retirer</button>
                </div>
              </li>
            ))}
          </ol>
          <div className="editor-foot">
            <button type="button" className="primary" disabled={!dirty} onClick={() => void save()}>{dirty ? "Enregistrer" : "Enregistré"}</button>
            <button type="button" className="danger" onClick={() => void remove()}>Supprimer la playlist</button>
          </div>
        </div>
        <aside className="card add-panel">
          <p className="section-label">Bibliothèque</p>
          {media.length > 4 ? <SearchField value={query} onChange={setQuery} placeholder="Chercher un média" /> : null}
          {media.length === 0 ? <p className="muted">Aucun média prêt. Envoyez-en depuis Médias.</p> : null}
          {media.length > 0 && available.length === 0 ? <p className="muted">Aucun média pour cette recherche.</p> : null}
          {available.map((item) => (
            <button key={item.id} type="button" className="add-line" onClick={() => add(item)}>
              <span>
                <strong>{item.originalName}</strong>
                <span className="meta">{item.type === "video" ? "Vidéo" : "Image"} · {formatBytes(item.size)}</span>
              </span>
              <span className="plus">Ajouter</span>
            </button>
          ))}
        </aside>
      </div>
    </div>
  );
}

function toDraft(item: PlaylistItem): DraftItem {
  return {
    key: item.id,
    mediaId: item.mediaId,
    name: item.name,
    type: item.type,
    size: item.size,
    durationSeconds: item.durationSeconds,
  };
}

function countLabel(playlist: Playlist): string {
  return `${plural(playlist.items.length, "média", "médias")} · ${plural(playlist.screenCount, "écran", "écrans")}`;
}

function plural(count: number, singular: string, many: string): string {
  return `${count} ${count === 1 ? singular : many}`;
}

function previewLabel(playlist: Playlist): string {
  if (playlist.items.length === 0) return "Vide — ajoutez des médias";
  const names = playlist.items.slice(0, 3).map((item) => item.name);
  const rest = playlist.items.length - names.length;
  return rest > 0 ? `${names.join("  ·  ")}  ·  +${rest}` : names.join("  ·  ");
}
